/**
 * Client-side mirror of API email-domain typo checks (soft-suggest + hard-block).
 * Keep in sync with apps/api/src/lib/email-domain-typos.ts
 */

export type EmailDomainTypoResult =
  | { ok: true }
  | { ok: false; blocked: boolean; suggestion?: string; message: string };

/** Obvious typo domains that must never be accepted (hard-block). */
const DENY_TO_CANONICAL: Record<string, string> = {
  // Gmail
  "gmai.com": "gmail.com",
  "gmial.com": "gmail.com",
  "gmal.com": "gmail.com",
  "gmail.con": "gmail.com",
  "gnail.com": "gmail.com",
  "gamil.com": "gmail.com",
  "gmail.co": "gmail.com",
  "gmaill.com": "gmail.com",
  // Hotmail
  "hotmial.com": "hotmail.com",
  "hotmal.com": "hotmail.com",
  "hotmai.com": "hotmail.com",
  "hotmail.con": "hotmail.com",
  "hotamil.com": "hotmail.com",
  "htmail.com": "hotmail.com",
  // Outlook
  "outlok.com": "outlook.com",
  "outloo.com": "outlook.com",
  "outlook.con": "outlook.com",
  "outllok.com": "outlook.com",
  "outlokk.com": "outlook.com",
  // iCloud
  "icoud.com": "icloud.com",
  "iclod.com": "icloud.com",
  "icloud.con": "icloud.com",
  "icluod.com": "icloud.com",
  // Yahoo
  "yaho.com": "yahoo.com",
  "yahooo.com": "yahoo.com",
  "yahoo.con": "yahoo.com",
  "yhaoo.com": "yahoo.com",
  "yaoo.com": "yahoo.com",
};

/** Only these may be offered as “Did you mean …?” (gmail/hotmail/outlook/yahoo/icloud). */
const SUGGEST_TARGETS = [
  "gmail.com",
  "hotmail.com",
  "outlook.com",
  "yahoo.com",
  "yahoo.co.uk",
  "icloud.com",
] as const;

/** Accepted as-is so aliases are not suggested toward another provider. */
const KNOWN_GOOD_DOMAINS = new Set<string>([
  ...SUGGEST_TARGETS,
  "googlemail.com",
  "live.com",
  "msn.com",
  "me.com",
  "mac.com",
  "btinternet.com",
  "sky.com",
  "aol.com",
  "protonmail.com",
  "proton.me",
]);

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function getEmailDomain(email: string): string | null {
  const normalized = normalizeEmail(email);
  const at = normalized.lastIndexOf("@");
  if (at < 1 || at === normalized.length - 1) return null;
  return normalized.slice(at + 1);
}

function levenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (a.length === 0) return b.length;
  if (b.length === 0) return a.length;
  const prev = new Array<number>(b.length + 1);
  const curr = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;
  for (let i = 1; i <= a.length; i++) {
    curr[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      curr[j] = Math.min(curr[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
    }
    for (let j = 0; j <= b.length; j++) prev[j] = curr[j]!;
  }
  return prev[b.length]!;
}

function softSuggestion(domain: string): string | undefined {
  if (KNOWN_GOOD_DOMAINS.has(domain)) return undefined;
  let best: string | undefined;
  let bestDist = Infinity;
  for (const good of SUGGEST_TARGETS) {
    const d = levenshtein(domain, good);
    // Near miss: edit distance 1, or distance 2 when lengths are close
    const maxDist = Math.abs(domain.length - good.length) <= 1 ? 2 : 1;
    if (d > 0 && d <= maxDist && d < bestDist) {
      bestDist = d;
      best = good;
    }
  }
  return best;
}

/**
 * Check an email for obvious domain typos.
 * - Hard-block: deny-list domains (blocked=true)
 * - Soft-suggest: near-miss of a known provider (blocked=false) — UI should confirm
 */
export function checkEmailDomainTypo(email: string): EmailDomainTypoResult {
  const domain = getEmailDomain(email);
  if (!domain) {
    return { ok: false, blocked: true, message: "Enter a valid email address." };
  }

  const canonical = DENY_TO_CANONICAL[domain];
  if (canonical) {
    return {
      ok: false,
      blocked: true,
      suggestion: canonical,
      message: `That email domain looks like a typo. Did you mean ${canonical}?`,
    };
  }

  const suggestion = softSuggestion(domain);
  if (suggestion) {
    return {
      ok: false,
      blocked: false,
      suggestion,
      message: `Did you mean ${suggestion}?`,
    };
  }

  return { ok: true };
}

export type SignupEmailTypoGate =
  | { status: "ok" }
  | { status: "blocked"; suggestion?: string; message: string }
  | { status: "soft"; suggestion: string; message: string };

/**
 * Signup-only gate. Soft-confirm applies only to the exact address the user
 * chose to keep — applying a suggestion, then editing the email, must re-check.
 * Hard-blocks cannot be confirmed.
 */
export function evaluateSignupEmailTypo(
  email: string,
  confirmedEmail: string | null
): SignupEmailTypoGate {
  const check = checkEmailDomainTypo(email);
  if (check.ok) return { status: "ok" };
  if (check.blocked) {
    return { status: "blocked", suggestion: check.suggestion, message: check.message };
  }
  if (check.suggestion) {
    if (confirmedEmail !== null && normalizeEmail(confirmedEmail) === normalizeEmail(email)) {
      return { status: "ok" };
    }
    return { status: "soft", suggestion: check.suggestion, message: check.message };
  }
  return { status: "ok" };
}
